import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, Sparkles, ListChecks, RefreshCw, Heart, CloudRain, Target, CalendarCheck,
  Mic, MessageCircle, Download, Copy, CheckCircle2, ArrowRight, FileText, Notebook,
} from 'lucide-react';

// 10 sections of the guide, written in Mickey's voice — direct, no fluff.
const SECTIONS = [
  {
    id: 'why',
    icon: BookOpen,
    title: 'Why journaling matters',
    body: [
      "Your thoughts move fast. Too fast to catch most days. Journaling is how you slow the spiral down enough to actually look at what your mind is doing.",
      "You don't journal because it's trendy. You journal because the thoughts you don't write down end up running you. The ones you DO write down — those are the ones you can finally argue with.",
      "Five minutes. A pen. A page. That's the whole tool kit.",
    ],
    callout: "Rule one: nobody else has to read this. Not your spouse, not your therapist, not the version of you that exists in two years. Just you, right now, getting honest.",
  },
  {
    id: 'styles',
    icon: ListChecks,
    title: 'Different journal styles (pick yours)',
    body: [
      "There's no 'right' way. There's just the way you'll actually do.",
    ],
    list: [
      { label: 'Stream-of-consciousness', desc: 'No structure. Just spill it. Best for clearing the noise.' },
      { label: 'Bullet journal', desc: 'Lists, tasks, mood. Best for organized minds.' },
      { label: 'Gratitude log', desc: '3-5 things daily. Best for shifting baseline mood.' },
      { label: 'Prompted', desc: 'Answer one question. Best when you don\u2019t know where to start.' },
      { label: 'Voice notes', desc: 'Talk it out. Best when writing feels like a chore.' },
    ],
  },
  {
    id: 'beginner-prompts',
    icon: Sparkles,
    title: 'Prompts for beginners',
    body: ["Start with one. Don\u2019t make it a project. Don\u2019t wait until you have a fancy notebook. Open your phone notes. Type. Move on."],
    prompts: [
      "What am I actually feeling right now? Not what I think I should be feeling.",
      "What\u2019s one thing I\u2019m grateful for that I usually take for granted?",
      "If today were a chapter title, what would it be?",
      "What\u2019s one thing I need to stop carrying?",
      "What did I learn about myself this week?",
    ],
  },
  {
    id: 'emotional-reset',
    icon: RefreshCw,
    title: 'Emotional reset prompts',
    body: ["For the days you can\u2019t shake it. The fog. The static. The 'why am I like this' afternoons."],
    prompts: [
      "What story am I telling myself about this situation? What might be untrue about it?",
      "If a friend told me what I\u2019m telling myself, what would I say to them?",
      "What\u2019s the smallest next step that doesn\u2019t require me to feel better first?",
      "What\u2019s the worst-case scenario \u2014 and could I actually survive it? (Spoiler: usually yes.)",
      "Name three things in this room. Name three sounds. You\u2019re here. Right now. That\u2019s the work.",
    ],
  },
  {
    id: 'gratitude',
    icon: Heart,
    title: 'Gratitude prompts',
    body: [
      "Gratitude isn\u2019t about pretending things are fine. It\u2019s about training your eyes to also see the parts that are.",
      "Five minutes a day. Watch what shifts in two weeks.",
    ],
    prompts: [
      "What\u2019s one small thing today that wouldn\u2019t exist without someone else\u2019s effort?",
      "What part of my body did its job today without me asking?",
      "Who in my life today made my life slightly easier?",
      "What\u2019s working in my life right now \u2014 even if just for today?",
      "What did past-me do that present-me is grateful for?",
    ],
  },
  {
    id: 'overthinking',
    icon: CloudRain,
    title: 'Overthinking dump pages',
    body: [
      "Sometimes the loop is too loud to argue with. That\u2019s when you don\u2019t analyze \u2014 you dump.",
      "Set a timer for 7 minutes. Don\u2019t stop writing. Don\u2019t edit. Don\u2019t pause. Get it out of your head and onto the page where it can\u2019t bother you anymore.",
    ],
    callout: "When the timer goes off, close the notebook. Walk away. Come back tomorrow if you want. Most of it will look different.",
    prompts: [
      "What\u2019s the loudest thought in my head right now?",
      "Everything I\u2019m worried about in the next 24 hours \u2014 list it all.",
      "What if I let it be messy?",
    ],
  },
  {
    id: 'goals',
    icon: Target,
    title: 'Goal tracking',
    body: [
      "Vague goals die quiet deaths. Specific goals you can see on paper outlive your motivation.",
      "One sentence per goal. One number. One date. That\u2019s the whole formula.",
    ],
    list: [
      { label: 'The Goal', desc: 'What \u2014 specifically? "Get fit" isn\u2019t a goal. "Walk 30 minutes daily through June" is.' },
      { label: 'The Why', desc: 'Why does this matter? If you can\u2019t answer, the goal won\u2019t survive a bad week.' },
      { label: 'The Next Step', desc: 'The smallest thing you could do today. Not this week. Today.' },
      { label: 'The Tripwire', desc: 'What happens if you miss two days? Have the plan BEFORE you miss.' },
    ],
  },
  {
    id: 'habits',
    icon: CalendarCheck,
    title: 'Habit tracking',
    body: [
      "You don\u2019t need an app. A row of squares and a marker beats a streak-anxiety app any day.",
      "Pick THREE habits. Not ten. Three. Track each day with an X. Miss a day? Don\u2019t miss two. That\u2019s the whole game.",
    ],
    prompts: [
      "What\u2019s ONE habit, if I did it every day for a year, that would change my life?",
      "What\u2019s ONE habit I do every day that\u2019s quietly hurting me?",
      "What\u2019s the smallest version of my habit I can\u2019t fail at? (1 push-up. 1 paragraph. 1 minute.)",
    ],
  },
  {
    id: 'voice',
    icon: Mic,
    title: 'Voice note journaling',
    body: [
      "Some days the pen is too slow. Open voice memos. Hit record. Talk to yourself like you\u2019re calling a trusted friend.",
      "Three minutes. Get it out. Delete it when you\u2019re done if you want. The point isn\u2019t the recording \u2014 it\u2019s the saying.",
    ],
    callout: "For privacy: keep voice notes local on your phone. Don\u2019t upload them anywhere. Your raw thoughts deserve a private home.",
  },
  {
    id: 'ai-mick',
    icon: MessageCircle,
    title: 'AI-assisted journaling with Mick',
    body: [
      "Sometimes you need a co-pilot. Mick \u2014 the free AI mindset coach \u2014 will sit with you, ask the questions a real friend would ask, and call out your BS when you need it.",
      "He\u2019s built on the HEADSPACE principles. Not therapy. Not a replacement for professional help. Just a thoughtful, no-fluff conversation when journaling solo feels stuck.",
    ],
    cta: { label: 'Open a session with Mick', href: '/coach' },
  },
];

const BEGINNER_TEMPLATE = `KICKASS COURSE \u2014 DAILY JOURNAL TEMPLATE\n\nDate: ____________________\n\n1) How am I actually feeling right now (one sentence)?\n   ______________________________________________________\n\n2) What's the loudest thought in my head?\n   ______________________________________________________\n\n3) Is that thought TRUE \u2014 or just LOUD?\n   ______________________________________________________\n\n4) One thing I'm grateful for today:\n   ______________________________________________________\n\n5) One small win from the last 24 hours:\n   ______________________________________________________\n\n6) One next step (small, today, doable):\n   ______________________________________________________\n\n----\nLive Life. Do Good. Be Happy. \u2014 Mickey\n`;

const RESET_TEMPLATE = `KICKASS COURSE \u2014 EMOTIONAL RESET TEMPLATE\n\nDate: ____________________\n\nWHAT'S GOING ON\n   ______________________________________________________\n\nTHE STORY I'M TELLING MYSELF\n   ______________________________________________________\n\nIS IT TRUE? (look for hard evidence, not feelings)\n   ______________________________________________________\n\nWHAT WOULD A TRUSTED FRIEND SAY?\n   ______________________________________________________\n\nWHAT'S ONE THING I CAN CONTROL RIGHT NOW?\n   ______________________________________________________\n\nGROUND. NAME 3 THINGS YOU CAN SEE/HEAR/FEEL.\n   1) ___________________________________________________\n   2) ___________________________________________________\n   3) ___________________________________________________\n\n----\nYou're not the thought. You're the one watching the thought. \u2014 Mickey\n`;

function downloadText(filename, content) {
  const blob = new Blob([content], { type: 'text/plain' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
}

export default function JournalGuide() {
  const [done, setDone] = useState(() => {
    try { return JSON.parse(localStorage.getItem('kc-journal-done') || '{}'); } catch { return {}; }
  });
  const [copiedIdx, setCopiedIdx] = useState('');

  useEffect(() => { localStorage.setItem('kc-journal-done', JSON.stringify(done)); }, [done]);

  const toggleDone = (id) => setDone({ ...done, [id]: !done[id] });
  const copy = async (text, key) => {
    try { await navigator.clipboard.writeText(text); setCopiedIdx(key); setTimeout(() => setCopiedIdx(''), 1500); } catch {}
  };
  const completed = Object.values(done).filter(Boolean).length;
  const pct = Math.round((completed / SECTIONS.length) * 100);

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative bg-ink text-white py-14 md:py-20 overflow-hidden" data-testid="journal-hero">
        <div className="absolute inset-0 pointer-events-none kc-noise opacity-50" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(700px 380px at 80% 50%, rgba(212,164,55,0.15), transparent 60%)' }} />
        <div className="relative kc-container grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--kc-gold)]/40 bg-[color:var(--kc-gold)]/10 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold"><Notebook size={12} /> The Guide</div>
            <h1 className="mt-4 font-display uppercase leading-[0.9]" style={{ fontSize: 'clamp(2.5rem, 6.5vw, 5.5rem)' }} data-testid="journal-title">HOW TO START<br /><span className="text-[color:var(--kc-gold)]">A JOURNAL</span> (THE RIGHT WAY).</h1>
            <p className="mt-4 text-lg text-white/80 max-w-xl leading-relaxed">No fancy notebook. No daily streak guilt. Just the practical, no-fluff playbook for the kind of journaling that actually moves the needle.</p>
          </div>
          <div className="lg:col-span-4">
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[var(--kc-radius-md)] p-5">
              <div className="text-[10px] tracking-[0.22em] uppercase text-white/60">Your progress</div>
              <div className="mt-2 flex items-baseline gap-2"><div className="font-display text-5xl text-[color:var(--kc-gold)]">{completed}</div><div className="text-white/70">of {SECTIONS.length} sections</div></div>
              <div className="mt-3 h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-[color:var(--kc-gold)] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
              <Link to="/resources" className="mt-4 inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-white/70 hover:text-[color:var(--kc-gold)]">Log today’s journal streak <ArrowRight size={12}/></Link>
            </div>
          </div>
        </div>
      </section>

      {/* TEMPLATES STRIP */}
      <section className="bg-white py-8 border-b border-kc" data-testid="journal-templates">
        <div className="kc-container flex flex-col md:flex-row md:items-center gap-4">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold">Free templates</div>
            <div className="font-display uppercase text-2xl">Print these. Use them tonight.</div>
          </div>
          <div className="md:ml-auto flex flex-wrap gap-2">
            <button onClick={() => downloadText('kickass-daily-journal.txt', BEGINNER_TEMPLATE)} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] border border-kc-strong hover:bg-surface-2 transition-colors text-sm font-semibold" data-testid="journal-dl-daily"><Download size={14}/> Daily template</button>
            <button onClick={() => downloadText('kickass-emotional-reset.txt', RESET_TEMPLATE)} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white hover:bg-black/90 transition-colors text-sm font-semibold" data-testid="journal-dl-reset"><Download size={14}/> Reset template</button>
          </div>
        </div>
      </section>

      {/* CONTENT GRID */}
      <section className="py-12 md:py-16">
        <div className="kc-container grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar nav */}
          <aside className="lg:col-span-3">
            <nav className="lg:sticky lg:top-20 space-y-1" data-testid="journal-sidebar">
              <div className="text-[10px] tracking-[0.22em] uppercase text-muted-kc font-semibold mb-2">Sections</div>
              {SECTIONS.map((s, i) => {
                const Icon = s.icon;
                const isDone = !!done[s.id];
                return (
                  <a key={s.id} href={`#${s.id}`} className={`flex items-center gap-2 px-3 py-2 rounded-[var(--kc-radius-sm)] text-sm transition-colors ${isDone ? 'bg-gold-soft text-ink font-semibold' : 'hover:bg-surface-2 text-ink-2'}`} data-testid={`journal-nav-${s.id}`}>
                    <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${isDone ? 'bg-[color:var(--kc-gold)] text-black' : 'bg-surface-2 text-muted-kc'}`}>{isDone ? <CheckCircle2 size={12}/> : i + 1}</span>
                    <Icon size={14} className="opacity-70" />
                    <span className="truncate">{s.title}</span>
                  </a>
                );
              })}
            </nav>
          </aside>

          {/* Sections */}
          <div className="lg:col-span-9 space-y-12" data-testid="journal-sections">
            {SECTIONS.map((s, i) => {
              const Icon = s.icon;
              const isDone = !!done[s.id];
              return (
                <article key={s.id} id={s.id} className="scroll-mt-20" data-testid={`journal-section-${s.id}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="font-display text-3xl text-[color:var(--kc-gold)]">{String(i + 1).padStart(2, '0')}</span>
                    <Icon size={22} className="text-ink-2" />
                    <h2 className="font-display uppercase text-2xl md:text-4xl leading-tight">{s.title}</h2>
                    <button onClick={() => toggleDone(s.id)} className={`ml-auto h-9 px-3 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${isDone ? 'bg-[color:var(--kc-gold)] text-black' : 'border border-kc-strong text-ink-2 hover:bg-surface-2'}`} data-testid={`journal-mark-${s.id}`}>{isDone ? <><CheckCircle2 size={12} className="inline mr-1"/> Done</> : 'Mark done'}</button>
                  </div>
                  <div className="space-y-3 max-w-3xl">
                    {(s.body || []).map((p, j) => <p key={j} className="text-lg text-ink-2 leading-relaxed">{p}</p>)}
                    {s.callout && (<div className="bg-ink text-white rounded-[var(--kc-radius-md)] p-5 mt-4"><div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold mb-2">Mickey says</div><div className="text-white/90 leading-relaxed">{s.callout}</div></div>)}
                    {s.list && (<ul className="mt-3 space-y-2">{s.list.map((it, j) => <li key={j} className="flex gap-3 bg-surface-2 rounded-[var(--kc-radius-sm)] p-3"><span className="font-display text-xl text-[color:var(--kc-gold)] w-6">{j + 1}</span><div><div className="font-semibold">{it.label}</div><div className="text-sm text-ink-2">{it.desc}</div></div></li>)}</ul>)}
                    {s.prompts && (<div className="mt-4 space-y-2" data-testid={`journal-prompts-${s.id}`}>{s.prompts.map((p, j) => { const key = `${s.id}-${j}`; return (<div key={j} className="group flex items-start gap-3 p-3 bg-white border border-kc rounded-[var(--kc-radius-sm)] hover:border-[color:var(--kc-gold)] transition-colors"><Sparkles size={14} className="text-[color:var(--kc-gold)] mt-1 flex-shrink-0" /><div className="flex-1 text-ink-2 italic">“{p}”</div><button onClick={() => copy(p, key)} className="text-xs font-semibold inline-flex items-center gap-1 text-muted-kc hover:text-ink" data-testid={`prompt-copy-${key}`}>{copiedIdx === key ? <><CheckCircle2 size={12}/> Copied</> : <><Copy size={12}/> Copy</>}</button></div>); })}</div>)}
                    {s.cta && (<Link to={s.cta.href} className="mt-4 inline-flex items-center gap-2 h-12 px-6 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid={`journal-cta-${s.id}`}>{s.cta.label} <ArrowRight size={16}/></Link>)}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-ink text-white py-12">
        <div className="kc-container flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)]">Now do the work</div>
            <div className="mt-1 font-display uppercase text-2xl md:text-3xl">Open the notebook. Five minutes. That’s it.</div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Link to="/resources" className="h-12 px-6 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] border border-white/20 hover:bg-white/10 font-semibold"><FileText size={16}/> Resources</Link>
            <Link to="/coach" className="h-12 px-6 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)]"><MessageCircle size={16}/> Journal with Mick</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
